import { extendTailwindMerge } from "tailwind-merge";

/**
 * Extended tailwind-merge configuration for DaisyUI v5
 *
 * This configuration ensures that DaisyUI component classes are properly
 * merged when using the cn() utility function. Classes in the same group
 * are treated as mutually exclusive - the last one in the list wins.
 *
 * @example
 * ```typescript
 * cn('btn btn-primary', isError && 'btn-error') // => 'btn btn-error'
 * cn('tooltip tooltip-top', isRight && 'tooltip-right') // => 'tooltip tooltip-right'
 * ```
 */
export const twMerge = extendTailwindMerge<
  // Button class groups
  | "btn-variant"
  | "btn-size"
  | "btn-state"
  | "btn-shape"
  | "btn-width"
  | "btn-glass"
  // Card class groups
  | "card-variant"
  | "card-image"
  // Badge class groups
  | "badge-variant"
  | "badge-size"

  // Table class groups
  | "table-size"
  | "table-variant"
  // Stats, Countdown, Rating
  | "stat-variant"
  | "countdown-variant"
  | "rating-size"
  | "rating-variant"
  // Menu & Navigation
  | "menu-size"
  | "menu-variant"
  | "navbar-variant"
  // Tabs
  | "tab-variant"
  | "tabs-size"
  // Breadcrumbs, Pagination, Steps
  | "breadcrumbs-variant"
  | "pagination-variant"
  | "step-variant"
  | "steps-variant"
  // Alert & Feedback
  | "alert-variant"
  | "loading-variant"
  | "loading-size"
  | "toast-position"
  // Progress
  | "progress-variant"
  | "radial-progress-variant"
  // Form inputs
  | "input-variant"
  | "input-size"
  | "textarea-variant"
  | "textarea-size"
  | "select-variant"
  | "select-size"
  | "checkbox-variant"
  | "checkbox-size"
  | "radio-variant"
  | "radio-size"
  | "toggle-variant"
  | "toggle-size"
  | "range-variant"
  | "range-size"
  | "file-input-variant"
  | "file-input-size"
  // Layout components
  | "drawer-position"
  | "drawer-state"
  | "modal-position"
  | "modal-state"
  | "dropdown-position"
  | "dropdown-state"
  | "collapse-variant"
  | "join-variant"
  | "join-item"
  | "stack-variant"
  | "indicator-position"
  | "divider-variant"
  | "divider-position"
  // Mockups
  | "mockup-phone-variant"
  | "mockup-browser-variant"
  | "mockup-code-variant"
  | "mockup-window-variant"
  // Other components
  | "avatar-variant"
  | "avatar-group"
  | "swap-variant"
  | "kbd-size"
  | "tooltip-variant"
  | "tooltip-position"
  | "tooltip-state"
  | "artboard-variant"
  | "artboard-size"
  | "diff-variant"
  | "mask-variant"
  | "chat-position"
  | "chat-bubble-variant"
  | "theme-controller"
>({
  extend: {
    classGroups: {
      // Actions - Buttons
      "btn-variant": [
        "btn-neutral",
        "btn-primary",
        "btn-secondary",
        "btn-accent",
        "btn-info",
        "btn-success",
        "btn-warning",
        "btn-error",
        "btn-ghost",
        "btn-link",
      ],
      "btn-state": ["btn-active", "btn-disabled"],
      "btn-size": ["btn-xs", "btn-sm", "btn-md", "btn-lg"],
      "btn-shape": ["btn-circle", "btn-square"],
      "btn-width": ["btn-block", "btn-wide"],
      "btn-glass": ["btn-glass"],

      // Data Display - Card
      "card-variant": [
        "card-normal",
        "card-compact",
        "card-side",
        "card-bordered",
      ],
      "card-image": ["image-full"],

      // Data Display - Badge
      "badge-variant": [
        "badge-neutral",
        "badge-primary",
        "badge-secondary",
        "badge-accent",
        "badge-info",
        "badge-success",
        "badge-warning",
        "badge-error",
        "badge-ghost",
        "badge-outline",
      ],
      "badge-size": ["badge-xs", "badge-sm", "badge-md", "badge-lg"],

      // Data Display - Table
      "table-size": ["table-xs", "table-sm", "table-md", "table-lg"],
      "table-variant": ["table-zebra", "table-pin-rows", "table-pin-cols"],

      // Data Display - Stats
      "stat-variant": ["stat-horizontal", "stat-vertical"],

      // Data Display - Countdown
      "countdown-variant": ["countdown"],

      // Data Display - Rating
      "rating-size": ["rating-xs", "rating-sm", "rating-md", "rating-lg"],
      "rating-variant": ["rating-half"],

      // Navigation - Menu
      "menu-size": ["menu-xs", "menu-sm", "menu-md", "menu-lg"],
      "menu-variant": ["menu-horizontal", "menu-vertical"],

      // Navigation - Navbar
      "navbar-variant": ["navbar-start", "navbar-center", "navbar-end"],

      // Navigation - Tabs
      "tab-variant": [
        "tab-lifted",
        "tab-bordered",
        "tab-boxed",
        "tab-active",
        "tab-disabled",
      ],
      "tabs-size": ["tabs-xs", "tabs-sm", "tabs-md", "tabs-lg"],

      // Navigation - Breadcrumbs
      "breadcrumbs-variant": ["breadcrumbs"],

      // Navigation - Pagination
      "pagination-variant": ["join-horizontal", "join-vertical"],

      // Navigation - Steps
      "step-variant": [
        "step-neutral",
        "step-primary",
        "step-secondary",
        "step-accent",
        "step-info",
        "step-success",
        "step-warning",
        "step-error",
      ],
      "steps-variant": ["steps-horizontal", "steps-vertical"],

      // Feedback - Alert
      "alert-variant": [
        "alert-info",
        "alert-secondary",
        "alert-success",
        "alert-warning",
        "alert-error",
      ],

      // Feedback - Loading
      "loading-variant": [
        "loading-spinner",
        "loading-dots",
        "loading-ring",
        "loading-ball",
        "loading-bars",
        "loading-infinity",
      ],
      "loading-size": ["loading-xs", "loading-sm", "loading-md", "loading-lg"],

      // Feedback - Toast
      "toast-position": [
        "toast-top",
        "toast-bottom",
        "toast-start",
        "toast-end",
        "toast-center",
        "toast-middle",
      ],

      // Feedback - Progress
      "progress-variant": [
        "progress-primary",
        "progress-secondary",
        "progress-accent",
        "progress-info",
        "progress-success",
        "progress-warning",
        "progress-error",
      ],

      // Feedback - Radial Progress
      "radial-progress-variant": [
        "radial-progress-primary",
        "radial-progress-secondary",
        "radial-progress-accent",
        "radial-progress-info",
        "radial-progress-success",
        "radial-progress-warning",
        "radial-progress-error",
      ],

      // Data Input - Input
      "input-variant": [
        "input-bordered",
        "input-ghost",
        "input-primary",
        "input-secondary",
        "input-accent",
        "input-info",
        "input-success",
        "input-warning",
        "input-error",
      ],
      "input-size": ["input-xs", "input-sm", "input-md", "input-lg"],

      // Data Input - Textarea
      "textarea-variant": [
        "textarea-bordered",
        "textarea-ghost",
        "textarea-primary",
        "textarea-secondary",
        "textarea-accent",
        "textarea-info",
        "textarea-success",
        "textarea-warning",
        "textarea-error",
      ],
      "textarea-size": [
        "textarea-xs",
        "textarea-sm",
        "textarea-md",
        "textarea-lg",
      ],

      // Data Input - Select
      "select-variant": [
        "select-bordered",
        "select-ghost",
        "select-primary",
        "select-secondary",
        "select-accent",
        "select-info",
        "select-success",
        "select-warning",
        "select-error",
      ],
      "select-size": ["select-xs", "select-sm", "select-md", "select-lg"],

      // Data Input - Checkbox
      "checkbox-variant": [
        "checkbox-primary",
        "checkbox-secondary",
        "checkbox-accent",
        "checkbox-info",
        "checkbox-success",
        "checkbox-warning",
        "checkbox-error",
      ],
      "checkbox-size": [
        "checkbox-xs",
        "checkbox-sm",
        "checkbox-md",
        "checkbox-lg",
        "checkbox-xl",
      ],

      // Data Input - Radio
      "radio-variant": [
        "radio-primary",
        "radio-secondary",
        "radio-accent",
        "radio-info",
        "radio-success",
        "radio-warning",
        "radio-error",
      ],
      "radio-size": [
        "radio-xs",
        "radio-sm",
        "radio-md",
        "radio-lg",
        "radio-xl",
      ],

      // Data Input - Toggle
      "toggle-variant": [
        "toggle-primary",
        "toggle-secondary",
        "toggle-accent",
        "toggle-info",
        "toggle-success",
        "toggle-warning",
        "toggle-error",
      ],
      "toggle-size": ["toggle-xs", "toggle-sm", "toggle-md", "toggle-lg"],

      // Data Input - Range
      "range-variant": [
        "range-primary",
        "range-secondary",
        "range-accent",
        "range-info",
        "range-success",
        "range-warning",
        "range-error",
      ],
      "range-size": ["range-xs", "range-sm", "range-md", "range-lg"],

      // Data Input - File Input
      "file-input-variant": [
        "file-input-bordered",
        "file-input-ghost",
        "file-input-primary",
        "file-input-secondary",
        "file-input-accent",
        "file-input-info",
        "file-input-success",
        "file-input-warning",
        "file-input-error",
      ],
      "file-input-size": [
        "file-input-xs",
        "file-input-sm",
        "file-input-md",
        "file-input-lg",
      ],

      // Layout - Drawer
      "drawer-position": ["drawer-end"],
      "drawer-state": ["drawer-open"],

      // Layout - Modal
      "modal-position": ["modal-top", "modal-middle", "modal-bottom"],
      "modal-state": ["modal-open"],

      // Layout - Dropdown
      "dropdown-position": [
        "dropdown-top",
        "dropdown-bottom",
        "dropdown-left",
        "dropdown-right",
        "dropdown-end",
      ],
      "dropdown-state": ["dropdown-open", "dropdown-hover"],

      // Layout - Collapse
      "collapse-variant": [
        "collapse-arrow",
        "collapse-plus",
        "collapse-open",
        "collapse-close",
      ],

      // Layout - Join
      "join-variant": ["join-horizontal", "join-vertical"],
      "join-item": ["join-item"],

      // Layout - Stack
      "stack-variant": ["stack"],

      // Layout - Indicator
      "indicator-position": [
        "indicator-top",
        "indicator-bottom",
        "indicator-start",
        "indicator-end",
        "indicator-center",
        "indicator-middle",
      ],

      // Layout - Divider
      "divider-variant": ["divider-horizontal", "divider-vertical"],
      "divider-position": ["divider-start", "divider-end"],

      // Mockup - Phone
      "mockup-phone-variant": ["mockup-phone"],

      // Mockup - Browser
      "mockup-browser-variant": ["mockup-browser"],

      // Mockup - Code
      "mockup-code-variant": ["mockup-code"],

      // Mockup - Window
      "mockup-window-variant": ["mockup-window"],

      // Other - Avatar
      "avatar-variant": [
        "avatar-online",
        "avatar-offline",
        "avatar-placeholder",
      ],
      "avatar-group": ["avatar-group"],

      // Other - Swap
      "swap-variant": ["swap-rotate", "swap-flip", "swap-active"],

      // Other - Kbd
      "kbd-size": ["kbd-xs", "kbd-sm", "kbd-md", "kbd-lg"],

      // Other - Tooltip
      "tooltip-variant": [
        "tooltip-primary",
        "tooltip-secondary",
        "tooltip-accent",
        "tooltip-info",
        "tooltip-success",
        "tooltip-warning",
        "tooltip-error",
      ],
      "tooltip-position": [
        "tooltip-top",
        "tooltip-bottom",
        "tooltip-left",
        "tooltip-right",
      ],
      "tooltip-state": ["tooltip-open"],

      // Other - Artboard
      "artboard-variant": ["artboard-horizontal", "artboard-demo"],
      "artboard-size": [
        "phone-1",
        "phone-2",
        "phone-3",
        "phone-4",
        "phone-5",
        "phone-6",
      ],

      // Other - Diff
      "diff-variant": ["diff-item-1", "diff-item-2", "diff-resizer"],

      // Other - Mask
      "mask-variant": [
        "mask-squircle",
        "mask-heart",
        "mask-hexagon",
        "mask-hexagon-2",
        "mask-decagon",
        "mask-pentagon",
        "mask-diamond",
        "mask-square",
        "mask-circle",
        "mask-parallelogram",
        "mask-parallelogram-2",
        "mask-parallelogram-3",
        "mask-parallelogram-4",
        "mask-star",
        "mask-star-2",
        "mask-triangle",
        "mask-triangle-2",
        "mask-triangle-3",
        "mask-triangle-4",
        "mask-half-1",
        "mask-half-2",
      ],

      // Other - Chat
      "chat-position": ["chat-start", "chat-end"],
      "chat-bubble-variant": [
        "chat-bubble-primary",
        "chat-bubble-secondary",
        "chat-bubble-accent",
        "chat-bubble-info",
        "chat-bubble-success",
        "chat-bubble-warning",
        "chat-bubble-error",
      ],

      // Other - Theme Controller
      "theme-controller": ["theme-controller"],
    },
  },
});
